// src/components/Fabricantes/FabricanteIndex.tsx

import { useState, useEffect, useRef } from "react";
import * as S from "./CatEstarcido.styles";

// Importaciones de DevExtreme
import DataGrid, {
  Column,
  HeaderFilter,
  Paging,
  Toolbar,
  Item,
  SearchPanel,
  FilterRow,
} from "devextreme-react/data-grid";
import { Popup } from "devextreme-react/popup";
import Form, { SimpleItem, Label, RequiredRule } from "devextreme-react/form";
import type { default as FormInstance } from "devextreme/ui/form";
import { Button } from "devextreme-react/button";
import { FiEdit } from "react-icons/fi";
import notify from "devextreme/ui/notify";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";
import axios, { AxiosError } from "axios";
import { ApiError } from "../../../services/apiError";
import { apiService } from "../../../services/apiService";
import { CatEstarcido } from "../../../dtos/CatEstarcido";
import { useNavigate } from "react-router-dom";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    const errorMessage =
      error.response?.data?.message || "Ocurrió un error en el servidor.";
    return {
      message: errorMessage,
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return {
    message: "Ocurrió un error inesperado. Revisa tu conexión de red.",
  };
};

const CatEstarcidoIndex = () => {
  const [dataSource, setDataSource] = useState<CatEstarcido[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormPopupVisible, setIsFormPopupVisible] = useState(false);

  const dataGridRef = useRef<any>(null);
  const [formMode, setFormMode] = useState<"add" | "edit">("add");
  const navigate = useNavigate();
  const [editingItem, setEditingItem] = useState<Partial<CatEstarcido> | null>(
    null,
  );

  const formRef = useRef<FormInstance | null>(null);
  const handleExportExcel = async () => {
    const gridInstance = dataGridRef.current?.instance();

    if (!gridInstance) {
      notify("No se pudo obtener la tabla para exportar.", "error", 3000);
      return;
    }

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Estarcidos");

    await exportDataGrid({
      component: gridInstance,
      worksheet,
      autoFilterEnabled: true,

      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell?.rowType === "header") {
          excelCell.font = { bold: true };
        }
      },
    });

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      "Estarcidos.xlsx",
    );
  };
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiService.get<CatEstarcido[]>(
        "/api/Catalogos/CatEstarcidos",
      );
      setDataSource(response);
    } catch (err: any) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAdd = () => {
    // Establecemos el modo en 'add'
    setFormMode("add");
    setEditingItem({});
    setIsFormPopupVisible(true);
  };

  const handleEdit = (data: CatEstarcido) => {
    // Establecemos el modo en 'edit'
    setFormMode("edit");
    setEditingItem({ ...data });
    setIsFormPopupVisible(true);
  };

  const handleSave = async () => {
    const form = formRef.current;
    if (!form) return;

    const validation = await form.validate();
    if (!validation.isValid) {
      notify(
        "Por favor, complete todos los campos requeridos.",
        "warning",
        2000,
      );
      return;
    }

    const formData = form.option("formData") as CatEstarcido;

    try {
      // La lógica ahora usa el estado 'formMode', que es infalible.
      if (formMode === "edit" && editingItem) {
        // Actualizar (PUT)
        const response = await apiService.put<CatEstarcido, CatEstarcido>(
          `/api/Catalogos/CatEstarcidos/${editingItem.codigo}`, // Usar el código original
          formData,
        );
        setDataSource((prev) =>
          prev.map((item) =>
            item.codigo === editingItem.codigo ? response : item,
          ),
        );
        notify("Estarcido actualizado con éxito.", "success", 2000);
      } else {
        // Crear (POST)
        const response = await apiService.post<CatEstarcido, CatEstarcido>(
          "/api/Catalogos/CatEstarcidos",
          formData,
        );
        setDataSource((prev) => [...prev, response]);
        notify("Estarcido creado con éxito.", "success", 2000);
      }

      setIsFormPopupVisible(false);
      setEditingItem(null);
    } catch (error) {
      notify(handleApiError(error).message, "error", 3000);
    }
  };

  const EstatusCell = (cellInfo: any) => {
    const isDeleted = cellInfo.value;
    const isActive = !isDeleted;
    return (
      <div
        style={{
          padding: "4px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 500,
          color: "#fff",
          backgroundColor: isActive ? "#16a34a" : "#71717a",
          textAlign: "center",
        }}
      >
        {isActive ? "Activo" : "Inactivo"}
      </div>
    );
  };

  const ActionsCell = (cellData: any) => {
    const item = cellData.data as CatEstarcido;
    return (
      <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
        <Button
          hint="Editar"
          onClick={() => handleEdit(item)}
          stylingMode="text"
        >
          <FiEdit size={18} color="#1d4ed8" />
        </Button>
      </div>
    );
  };

  return (
    <S.PageWrapper>
      <S.GlobalPopupStyles />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <Button
          icon="chevronleft"
          text="Regresar"
          stylingMode="outlined"
          type="default"
          onClick={() => navigate(-1)} // ← vuelve a la pestaña anterior
        />
      </div>
      <S.Header>Catálogo de Estarcidos</S.Header>
      <DataGrid
        ref={dataGridRef}
        dataSource={dataSource}
        keyExpr="codigo"
        showBorders={true}
        columnAutoWidth={true}
        hoverStateEnabled={true}
        loadPanel={{ enabled: loading }}
      >
        <FilterRow visible={true} />
        <HeaderFilter visible={true} allowSearch={true} />
        <Paging defaultPageSize={50} />
        <Toolbar>
          <Item name="searchPanel" />
          <Item
            location="after"
            widget="dxButton"
            options={{
              text: "Exportar Excel",
              icon: "xlsxfile",
              stylingMode: "contained",
              type: "default",
              onClick: handleExportExcel,
            }}
          />
          <Item
            location="after"
            widget="dxButton"
            options={{
              text: "Añadir Estarcido",
              icon: "add",
              stylingMode: "contained",
              type: "default",
              onClick: handleAdd,
            }}
          />
        </Toolbar>
        <SearchPanel
          visible={true}
          highlightCaseSensitive={true}
          placeholder="Buscar..."
        />
        <Column dataField="codigo" caption="Código" width={250} />
        <Column dataField="color" caption="Color" width={250} />
        <Column dataField="tipoPintura" caption="Tipo pintura" width={250} />
        <Column
          dataField="estatusActivo"
          caption="Estatus"
          alignment="center"
          cellRender={EstatusCell}
          visible={false}
          width={250}
        />
        <Column
          caption="Acciones"
          alignment="center"
          width={250}
          allowExporting={false}
          cellRender={ActionsCell}
        />
      </DataGrid>

      <Popup
        visible={isFormPopupVisible}
        onHiding={() => setIsFormPopupVisible(false)}
        dragEnabled={false}
        showCloseButton={true}
        showTitle={true}
        title={
          formMode === "edit"
            ? `Editar Estarcido: ${editingItem?.tipoPintura}`
            : "Añadir nuevo Estarcido"
        }
        width={500}
        height="auto"
        className="custom-form-popup"
      >
        <S.FormContainer>
          <Form
            formData={editingItem}
            onInitialized={(e) => {
              formRef.current = e.component ?? null;
            }}
          >
            <SimpleItem dataField="codigo">
              <Label text="Código" />
              <RequiredRule message="El código es requerido." />
            </SimpleItem>
            <SimpleItem dataField="color">
              <Label text="Color" />
              <RequiredRule message="El color es requerido." />
            </SimpleItem>
            <SimpleItem dataField="tipoPintura">
              <Label text="Tipo de pintura" />
              <RequiredRule message="El tipo de pintura es requerido." />
            </SimpleItem>
          </Form>
        </S.FormContainer>

        <S.PopupFooter>
          <S.CancelButton onClick={() => setIsFormPopupVisible(false)}>
            Cancelar
          </S.CancelButton>
          <S.SaveButton onClick={handleSave}>Guardar</S.SaveButton>
        </S.PopupFooter>
      </Popup>
    </S.PageWrapper>
  );
};

export default CatEstarcidoIndex;
